import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faSearch,
	faSpinner,
	faExclamationTriangle,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import EmptyState from "./EmptyState";
import {
	getSemesterMonths,
	getMaxPoints,
	calculateSubjectTotal,
	filterBySearchTerm,
} from "../utils";
import styles from "../GradesPage.module.css";

const StudentsView = ({
	selectedStudent,
	selectedSemester,
	searchTerm,
	setSearchTerm,
	grades,
	classData,
	isLoadingGrades,
	gradesError,
	departmentSubjects,
}) => {
	if (!selectedStudent) {
		return (
			<EmptyState
				icon={faUsers}
				title="Select a Student"
				message="Choose a student from the sidebar to view their grades across all subjects"
			/>
		);
	}

	const semesterMonths = getSemesterMonths(selectedSemester);

	// Filter subjects based on search term
	const filteredSubjects = filterBySearchTerm(departmentSubjects, searchTerm, [
		"name",
	]);

	return (
		<>
			<div className={styles.contentHeader}>
				<div className={styles.contentTitle}>
					<h2>
						{selectedStudent.name} ({selectedStudent.studentId})
					</h2>
					<span className={styles.semesterLabel}>
						{selectedSemester === 1 ? "1st" : "2nd"} Semester - All Subjects
					</span>
				</div>
				<div className={styles.searchContainer}>
					<FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
					<input
						type="text"
						placeholder="Search subjects..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className={styles.searchInput}
					/>
				</div>
			</div>

			{isLoadingGrades ? (
				<div className={styles.loadingContainer}>
					<FontAwesomeIcon icon={faSpinner} className={styles.spinner} spin />
					<p>Loading student grades...</p>
				</div>
			) : gradesError ? (
				<div className={styles.errorContainer}>
					<FontAwesomeIcon
						icon={faExclamationTriangle}
						className={styles.errorIcon}
					/>
					<p>Error loading student grades. Please try again.</p>
				</div>
			) : (
				<div className={styles.gradesTableContainer}>
					<div className={styles.gradesTable}>
						<div className={styles.tableHeader}>
							<div className={styles.studentNameColumn}>Subject</div>
							{semesterMonths.map((month) => (
								<div key={month.id} className={styles.monthColumn}>
									<span className={styles.monthName}>{month.short}</span>
									<span className={styles.monthMaxPoints}>
										/{getMaxPoints(classData.gradeLevel, month.id)} pts
									</span>
								</div>
							))}
							<div className={styles.totalColumn}>Total</div>
						</div>

						<div className={styles.tableBody}>
							{filteredSubjects.map((subject) => {
								const subjectKey = `${selectedStudent.id}_${subject.id}`;
								const subjectData = grades[subjectKey];

								return (
									<div key={subject.id} className={styles.studentRow}>
										<div className={styles.studentNameCell}>
											<span className={styles.studentName}>{subject.name}</span>
											<span className={styles.studentId}>
												Grade {classData.gradeLevel}
											</span>
										</div>

										{semesterMonths.map((month) => {
											const monthGrade = subjectData?.months?.[month.id];
											const hasGrades = monthGrade && monthGrade.total > 0;

											return (
												<div
													key={month.id}
													className={`${styles.gradeCell} ${
														hasGrades ? styles.hasGrades : styles.noGrades
													}`}
												>
													<span className={styles.gradeValue}>
														{monthGrade?.total || 0}
													</span>
													<span className={styles.gradeMax}>
														/{getMaxPoints(classData.gradeLevel, month.id)}
													</span>
												</div>
											);
										})}

										<div className={styles.totalCell}>
											<span className={styles.totalValue}>
												{calculateSubjectTotal(
													grades,
													subjectKey,
													selectedSemester
												)}
											</span>
											<span className={styles.totalMax}>/100</span>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default StudentsView;
